import { api, endpoints } from "api/api";
import { AxiosPromise, AxiosResponse } from "axios";
import { DocumentTemplateFormValues } from "pages/Documents/DocumentTemplateForm";
import { BaseApiPaging } from "./types";
import { PageFormValues } from "pages/Documents/PagePlaceholdersForm";

const fetch = (
  skip: number,
  take: number,
  searchPhrase: string,
  filter: string = "",
  orderBy: string = "",
): AxiosPromise<BaseApiPaging<DocumentTemplateApi>> => {
  return api.get<BaseApiPaging<DocumentTemplateApi>>(
    endpoints.DOCUMENT_TEMPLATES.GET(skip, take, searchPhrase, filter, orderBy),
  );
};

const fetchById = (id: number): AxiosPromise<DocumentTemplateApi> => {
  return api.get<DocumentTemplateApi>(endpoints.DOCUMENT_TEMPLATES.GET_BY_ID(id));
};

const create = (data: DocumentTemplateFormValues): AxiosPromise<number> => {
  return api.post(endpoints.DOCUMENT_TEMPLATES.CREATE, data);
};

const update = (id: number, data: DocumentTemplateFormValues): AxiosPromise => {
  return api.put(endpoints.DOCUMENT_TEMPLATES.UPDATE(id), data);
};

const download = (documentTemplateId: number): AxiosPromise<AxiosResponse<Blob>> => {
  return api.get(endpoints.DOCUMENT_TEMPLATES.DOWNLOAD(documentTemplateId), {
    responseType: "blob",
  });
};

const createPage = (documentTemplateId: number, data: PageFormValues): AxiosPromise => {
  const { placeholders, background, ...request } = data;
  const formData = new FormData();

  if (typeof background !== "string") formData.append("backgroundImage", background as File);

  data.placeholders.forEach((placeholder, index) => {
    formData.append(`placeholders[${index}].id`, placeholder.id?.toString() ?? "");
    formData.append(`placeholders[${index}].left`, placeholder.left?.toString() ?? "");
    formData.append(`placeholders[${index}].type`, placeholder.type?.toString() ?? "");
    formData.append(`placeholders[${index}].name`, placeholder.name?.toString() ?? "");
    formData.append(`placeholders[${index}].example`, placeholder.example?.toString() ?? "");
    formData.append(`placeholders[${index}].top`, placeholder.top?.toString() ?? "");
    formData.append(`placeholders[${index}].width`, placeholder.width?.toString() ?? "");
    formData.append(`placeholders[${index}].height`, placeholder.height?.toString() ?? "");
    formData.append(`placeholders[${index}].fontSize`, placeholder.fontSize?.toString() ?? "");
    formData.append(`placeholders[${index}].color`, placeholder.color?.toString() ?? "");
    formData.append(`placeholders[${index}].fontFamily`, placeholder.fontFamily?.toString() ?? "");
    formData.append(`placeholders[${index}].fontBold`, placeholder.fontBold?.toString() ?? "");
    formData.append(`placeholders[${index}].uppercase`, placeholder.uppercase?.toString() ?? "");
    formData.append(`placeholders[${index}].verticalAlignment`, placeholder.verticalAlignment?.toString() ?? "");
    formData.append(`placeholders[${index}].textAlignment`, placeholder.textAlignment?.toString() ?? "");
  });
  Object.entries(request).map(([key, value]) => {
    //@ts-ignore
    formData.append(key, value);
  });

  return api.post(endpoints.DOCUMENT_TEMPLATES.CRAETE_PAGE(documentTemplateId), formData);
};

const updatePage = (documentTemplateId: number, pageId: number, data: PageFormValues): AxiosPromise => {
  const { placeholders, background, ...request } = data;
  const formData = new FormData();

  if (typeof background !== "string") formData.append("backgroundImage", background as File);

  data.placeholders.forEach((placeholder, index) => {
    formData.append(`placeholders[${index}].id`, placeholder.id?.toString() ?? "");
    formData.append(`placeholders[${index}].left`, placeholder.left?.toString() ?? "");
    formData.append(`placeholders[${index}].type`, placeholder.type?.toString() ?? "");
    formData.append(`placeholders[${index}].name`, placeholder.name?.toString() ?? "");
    formData.append(`placeholders[${index}].example`, placeholder.example?.toString() ?? "");
    formData.append(`placeholders[${index}].top`, placeholder.top?.toString() ?? "");
    formData.append(`placeholders[${index}].width`, placeholder.width?.toString() ?? "");
    formData.append(`placeholders[${index}].height`, placeholder.height?.toString() ?? "");
    formData.append(`placeholders[${index}].fontSize`, placeholder.fontSize?.toString() ?? "");
    formData.append(`placeholders[${index}].color`, placeholder.color?.toString() ?? "");
    formData.append(`placeholders[${index}].fontFamily`, placeholder.fontFamily?.toString() ?? "");
    formData.append(`placeholders[${index}].fontBold`, placeholder.fontBold?.toString() ?? "");
    formData.append(`placeholders[${index}].uppercase`, placeholder.uppercase?.toString() ?? "");
    formData.append(`placeholders[${index}].verticalAlignment`, placeholder.verticalAlignment?.toString() ?? "");
    formData.append(`placeholders[${index}].textAlignment`, placeholder.textAlignment?.toString() ?? "");
  });
  Object.entries(request).map(([key, value]) => {
    //@ts-ignore
    formData.append(key, value);
  });

  return api.put(endpoints.DOCUMENT_TEMPLATES.UPDATE_PAGE(documentTemplateId, pageId), formData);
};

const deleteTemplate = (id: number): Promise<void> => {
  return api.delete(endpoints.DOCUMENT_TEMPLATES.DELETE(id));
};

const deletePage = (documentTemplateId: number, pageId: number): Promise<void> => {
  return api.delete(endpoints.DOCUMENT_TEMPLATES.DELETE_PAGE(documentTemplateId, pageId));
};

export const documentTemplatesApi = {
  fetch,
  fetchById,
  create,
  download,
  update,
  updatePage,
  createPage,
  delete: deleteTemplate,
  deletePage,
};

export interface DocumentTemplateApi {
  id: number;
  name: string;
  width: string;
  isActive: boolean;
  height: string;
  type: { id: number; name: string };
  pages: DocumentTemplatePageApi[];
}
export interface DocumentTemplatePageApi {
  pageId: number;
  pageNumber: number;
  background: string;
  placeholders: DocumentTemplatePlaceholderApi[];
}

export interface DocumentTemplatePlaceholderApi {
  id: number;
  type: number;
  name: string;
  example: string;
  width: string;
  height: string;
  fontBold: boolean;
  uppercase?: boolean;
  top: string;
  left: string;
  fontSize: string;
  color: string;
  fontFamily: string;
  verticalAlignment: number;
  textAlignment: number;
}
